/**
 * The encryption screens: first run, unlock, and recovery.
 *
 * The design handoff contains none of this — no key management, no recovery
 * phrase, no unlock. It is originated here, following the Mauvely voice from
 * the rest of the prototype: sentence case, titles ending in a full stop, mono
 * metadata, a one-line reassurance under anything that sounds alarming.
 *
 * The copy is doing real work. This is the only point where a user can lock
 * themselves out of their own data permanently, so the phrase screen is
 * deliberately hard to skip past: it cannot be dismissed without either
 * confirming three words or explicitly choosing to do it later, and the app
 * keeps asking until `recovery_confirmed_at` is set.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError, errorText, type PasskeyWire } from '../api';
import { useApp } from '../state';
import { ds } from '../ds';
import { display, mono, monoPlain, panel, RADIUS, SHADOW } from '../theme';
import {
  confirmationPositions,
  generatePhrase,
  PhraseError,
  phraseToEntropy,
  suggestWords
} from '../crypto/bip39';
import { createVault, WrongSecretError } from '../crypto/keys';
import { encryptName } from '../crypto/metadata';
import { generateNodeKey } from '../crypto/keys';
import { adopt, signOut, unlock, unlockWithMaster } from '../crypto/vault';
import { masterKeyFrom, prfSupported, unlockWithPasskey } from '../crypto/passkey';
import { cacheNodeKey } from '../nodes';
import type { Bytes } from '../crypto/primitives';
import { useT } from '../i18n';

// The app's own gate, in the app's own window — so the app ground, not the
// site's. Unlocking swaps this for `components/Shell` and the ground has to be
// the one thing that does not change underneath.
const Shell = ({ children }: { children: React.ReactNode }) => (
  <div
    className="mv-app"
    style={{
      minHeight: '100vh',
      background: 'var(--app-ground)',
      display: 'grid',
      placeItems: 'center',
      padding: '40px 20px'
    }}
  >
    <div style={{ ...panel, width: '100%', maxWidth: 560, padding: '34px 38px 30px' }}>
      {children}
    </div>
  </div>
);

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={{ ...mono, marginBottom: 10 }}>{children}</div>
);

const Lead = ({ children }: { children: React.ReactNode }) => (
  <p
    style={{
      fontFamily: "var(--font-display), 'Baloo 2', system-ui",
      fontWeight: 400,
      fontSize: 18,
      lineHeight: 1.35,
      color: 'var(--text-body)',
      margin: '10px 0 0'
    }}
  >
    {children}
  </p>
);

const Note = ({ tone = 'muted', children }: { tone?: 'muted' | 'bad'; children: React.ReactNode }) => (
  <p
    style={{
      fontSize: 13.5,
      lineHeight: 1.55,
      color: tone === 'bad' ? 'var(--danger-600)' : 'var(--text-muted)',
      margin: '12px 0 0'
    }}
  >
    {children}
  </p>
);

// ── first run ───────────────────────────────────────────────────────────────

type SetupStage = 'intro' | 'phrase' | 'confirm' | 'working';

/**
 * Build a vault.
 *
 * Everything happens in this component and nothing sensitive leaves it: the
 * password derives the key-encryption key, the phrase derives the recovery key,
 * both wrap a master key generated here, and only the wrapped forms are posted.
 */
export function VaultSetup() {
  const t = useT();
  const { me, refresh } = useApp();
  const { Button, Input, Icon } = ds();

  const [stage, setStage] = useState<SetupStage>('intro');
  const [password, setPassword] = useState('');
  const [phrase, setPhrase] = useState<string[]>([]);
  const [entropy, setEntropy] = useState<Bytes | null>(null);
  const [positions, setPositions] = useState<number[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);

  const begin = async () => {
    setError(null);
    if (password.length < 10) {
      setError('Enter the password you use for your Mauvely account.');
      return;
    }
    const generated = await generatePhrase();
    setPhrase(generated.phrase);
    setEntropy(generated.entropy);
    setPositions(confirmationPositions(3));
    setStage('phrase');
  };

  const finish = async (skipConfirmation: boolean) => {
    if (!entropy) return;
    setError(null);

    if (!skipConfirmation) {
      const wrong = positions.filter(
        (p) => (answers[p] ?? '').trim().toLowerCase() !== phrase[p]
      );
      if (wrong.length) {
        setError('That does not match what we showed you. Check the words and try again.');
        return;
      }
    }

    setStage('working');
    try {
      const { blobs, vault, rootNodeKey } = await createVault(password, entropy);

      // The root folder's name is encrypted like any other, so it needs a name
      // even though no screen ever shows it.
      const rootNameEnc = await encryptName(rootNodeKey, 'My files');

      const created = await api.vault.setup({ ...blobs, rootNameEnc });

      cacheNodeKey(created.rootNodeId, rootNodeKey);
      await adopt(vault);
      if (!skipConfirmation) await api.vault.confirmRecovery().catch(() => {});

      // Drop the phrase from memory now that it has done its job. It exists on
      // paper or it does not exist.
      setPhrase([]);
      setEntropy(null);
      setPassword('');

      await refresh();
    } catch (err) {
      setStage('confirm');
      setError(errorText(err, 'Could not set up encryption. Try again.'));
    }
  };

  if (stage === 'working') {
    return (
      <Shell>
        <Eyebrow>{t('vault.setting-up')}</Eyebrow>
        <h1 style={display(30)}>{t('vault.generating-your-keys')}</h1>
        <Lead>{t('vault.this-takes-a-moment')}</Lead>
      </Shell>
    );
  }

  if (stage === 'intro') {
    return (
      <Shell>
        <Eyebrow>{t('vault.welcome-to-cloud')}</Eyebrow>
        <h1 style={display(32)}>{t('vault.lets-encrypt-your-files')}</h1>
        <Lead>
          {t('vault.everything-you-put-in-mauvely')}
        </Lead>

        <div style={{ marginTop: 24 }}>
          <Input
            label={t('vault.your-mauvely-password')}
            type="password"
            autoComplete="current-password"
            value={password}
            hint={t('vault.the-same-password-you-signed')}
            onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
          />
        </div>

        {error && <Note tone="bad">{error}</Note>}

        <div style={{ marginTop: 22 }}>
          <Button variant="primary" size="lg" fullWidth onClick={begin}>
            {t('vault.continue')}
          </Button>
        </div>

        <Note>
          Signed in as <strong>{me?.user.email}</strong>.
        </Note>
      </Shell>
    );
  }

  if (stage === 'phrase') {
    return (
      <Shell>
        <Eyebrow>{t('vault.recovery-phrase')}</Eyebrow>
        <h1 style={display(30)}>{t('vault.write-these-24-words-down')}</h1>
        <Lead>
          {t('vault.if-you-ever-forget-your')}
        </Lead>

        <div
          style={{
            marginTop: 22,
            padding: '18px 20px',
            background: 'var(--surface-sunken)',
            borderRadius: RADIUS.card,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px 16px'
          }}
        >
          {phrase.map((word, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ ...monoPlain, color: 'var(--text-quiet)', minWidth: 18 }}>
                {i + 1}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono), monospace",
                  fontSize: 13,
                  color: 'var(--text-strong)'
                }}
              >
                {word}
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigator.clipboard?.writeText(phrase.join(' '))}
          >
            {t('vault.copy')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // A print dialogue is the least-bad "get this onto paper" path
              // that does not involve writing the phrase to a file.
              const w = window.open('', '_blank', 'width=600,height=700');
              if (!w) return;
              w.document.write(
                `<pre style="font:14px monospace;padding:40px;line-height:2">Mauvely Cloud recovery phrase\n\n${phrase
                  .map((word, i) => `${String(i + 1).padStart(2)}. ${word}`)
                  .join('\n')}\n\nKeep this somewhere safe and offline.</pre>`
              );
              w.print();
            }}
          >
            {t('vault.print')}
          </Button>
        </div>

        <Note>
          {t('vault.anyone-with-these-words-can')}
        </Note>

        <div style={{ marginTop: 22 }}>
          <Button variant="primary" size="lg" fullWidth onClick={() => setStage('confirm')}>
            {t('vault.ive-written-them-down')}
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Eyebrow>{t('vault.recovery-phrase')}</Eyebrow>
      <h1 style={display(30)}>{t('vault.check-three-of-them')}</h1>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 22 }}>
        {positions.map((p) => (
          <Input
            key={p}
            label={`Word ${p + 1}`}
            value={answers[p] ?? ''}
            autoComplete="off"
            onChange={(e) =>
              setAnswers((a) => ({ ...a, [p]: (e.target as HTMLInputElement).value }))
            }
          />
        ))}
      </div>

      {error && <Note tone="bad">{error}</Note>}

      <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
        <Button variant="primary" size="lg" onClick={() => finish(false)}>
          {t('vault.confirm-and-continue')}
        </Button>
        <Button variant="ghost" size="lg" onClick={() => setStage('phrase')}>
          {t('vault.show-the-words-again')}
        </Button>
      </div>

      <div style={{ marginTop: 14 }}>
        <span
          style={{ ...monoPlain, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => finish(true)}
        >
          {t('vault.skip-for-now')}
        </span>
        <Note>
          <Icon name="alert-triangle" size={13} /> Skipping leaves your phrase unconfirmed.
        </Note>
      </div>
    </Shell>
  );
}

// ── unlock ──────────────────────────────────────────────────────────────────

/**
 * Unlock an existing vault.
 *
 * Reached on every fresh session, because the server cannot keep the key. The
 * "trust this device" option persists the master key as a non-extractable
 * CryptoKey in IndexedDB — usable, never readable — see crypto/vault.ts.
 */
export function VaultUnlock() {
  const t = useT();
  const { me } = useApp();
  const { Button, Input, Checkbox } = ds();

  const [password, setPassword] = useState('');
  const [trust, setTrust] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'password' | 'recovery'>('password');
  const [passkeys, setPasskeys] = useState<PasskeyWire[]>([]);

  /**
   * Offer the passkey button only when there is one to use and the browser can
   * actually do PRF.
   *
   * Both halves matter. Showing it without a credential leads to an empty
   * chooser; showing it in a browser without PRF leads to a prompt that
   * succeeds and then cannot derive a key, which reads as data loss. Where
   * either is false the screen is exactly the password screen it always was.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!(await prfSupported())) return;
      try {
        const { passkeys: enrolled } = await api.vault.passkeys.list();
        if (!cancelled) setPasskeys(enrolled);
      } catch {
        // No passkeys, or the list failed. The password is right there.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const blobs = await api.vault.get();
      await unlock({ password }, blobs, { trustDevice: trust });
      setPassword('');
    } catch (err) {
      setError(
        err instanceof WrongSecretError
          ? 'That password does not open your files.'
          : err instanceof ApiError
            ? err.message
            : 'Could not unlock. Try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  const unlockByPasskey = async () => {
    setBusy(true);
    setError(null);
    try {
      const masterRaw = await unlockWithPasskey(passkeys);
      if (!masterRaw) {
        setError('That passkey could not open your vault. Use your password instead.');
        return;
      }
      const blobs = await api.vault.get();
      await unlockWithMaster(await masterKeyFrom(masterRaw), blobs, { trustDevice: trust });
      // Display only, and never worth failing an unlock over.
      void api.vault.passkeys.touch(passkeys[0]!.id).catch(() => {});
    } catch (err) {
      // A cancelled prompt throws NotAllowedError, which is not an error the
      // user needs explaining — they just changed their mind.
      if (err instanceof DOMException && err.name === 'NotAllowedError') return;
      setError(errorText(err, 'Could not unlock with that passkey.'));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'recovery') return <VaultRecover onCancel={() => setMode('password')} />;

  return (
    <Shell>
      <Eyebrow>{t('vault.encrypted')}</Eyebrow>
      <h1 style={display(30)}>{t('vault.unlock-your-files')}</h1>
      <Lead>{t('vault.enter-your-password-to-decrypt')}</Lead>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void submit();
        }}
      >
        <div style={{ marginTop: 24 }}>
          <Input
            label={t('vault.your-mauvely-password')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{ marginTop: 14 }}>
          <Checkbox
            label={t('vault.trust-this-device')}
            description="Not for a shared or public computer."
            checked={trust}
            onChange={(next: boolean) => setTrust(next)}
          />
        </div>

        {error && <Note tone="bad">{error}</Note>}

        <div style={{ marginTop: 20 }}>
          <Button variant="primary" size="lg" fullWidth type="submit" disabled={busy}>
            {busy ? 'Unlocking…' : 'Unlock'}
          </Button>
        </div>

        {passkeys.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              type="button"
              disabled={busy}
              onClick={() => void unlockByPasskey()}
            >
              {t('vault.unlock-with-a-passkey')}
            </Button>
          </div>
        )}
      </form>

      <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between' }}>
        <span
          style={{ ...monoPlain, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => setMode('recovery')}
        >
          {t('vault.use-your-recovery-phrase')}
        </span>
        <span style={monoPlain}>{me?.user.email}</span>
      </div>

      <div style={{ marginTop: 10 }}>
        <span
          style={{ ...monoPlain, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => void signOutOfCloud()}
        >
          {t('vault.not-you-sign-out')}
        </span>
      </div>
    </Shell>
  );
}

// ── recovery ────────────────────────────────────────────────────────────────

/**
 * Unlock with the 24 words.
 *
 * It used to offer a new password as well, written straight over the old wrap
 * with nothing checking it — one of the ways "my password stopped working"
 * happened. A password belongs to the account now (plan 23), so this only
 * opens the files.
 */
export function VaultRecover({ onCancel }: { onCancel?: () => void }) {
  const t = useT();
  const { Button, Input } = ds();

  const [words, setWords] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wordCount = useMemo(() => words.trim().split(/\s+/).filter(Boolean).length, [words]);
  const suggestion = useMemo(() => {
    const last = words.trim().split(/\s+/).pop() ?? '';
    return last.length >= 2 && wordCount <= 24 ? suggestWords(last, 3) : [];
  }, [words, wordCount]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const entropy = await phraseToEntropy(words);
      await unlock({ entropy }, await api.vault.get());
      setWords('');
    } catch (err) {
      setError(
        err instanceof PhraseError
          ? err.message
          : err instanceof WrongSecretError
            ? 'That phrase does not open this account’s files.'
            : err instanceof ApiError
              ? err.message
              : 'Could not recover. Check the words and try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <Eyebrow>{t('vault.recovery')}</Eyebrow>
      <h1 style={display(30)}>{t('vault.enter-your-24-words')}</h1>
      <Lead>
        {t('vault.in-the-order-you-wrote')}
      </Lead>

      <div style={{ marginTop: 22 }}>
        <Input
          label={t('vault.recovery-phrase')}
          multiline
          rows={4}
          value={words}
          autoComplete="off"
          hint={`${wordCount} of 24 words`}
          onChange={(e) => setWords((e.target as HTMLTextAreaElement).value)}
        />
      </div>

      {suggestion.length > 0 && (
        <div style={{ ...monoPlain, marginTop: 8 }}>
          Did you mean {suggestion.join(' · ')}?
        </div>
      )}

      {error && <Note tone="bad">{error}</Note>}

      <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
        <Button
          variant="primary"
          size="lg"
          disabled={busy || wordCount !== 24}
          onClick={() => void submit()}
        >
          {busy ? 'Checking…' : 'Recover my files'}
        </Button>
        {onCancel && (
          <Button variant="ghost" size="lg" onClick={onCancel}>
            {t('vault.back')}
          </Button>
        )}
      </div>
    </Shell>
  );
}

// ── signed out ──────────────────────────────────────────────────────────────

/** Cloud has no sign-in form of its own — mauvely.com owns accounts, and the
 *  shared cookie means arriving back here is already signed in. */
export function SignedOut() {
  const t = useT();
  const { Button } = ds();
  const returnTo = typeof window !== 'undefined' ? window.location.href : '';

  useEffect(() => {
    // Bounce automatically, but leave the button for anyone who lands here with
    // a blocked redirect or a paused screen reader.
    const timer = setTimeout(() => {
      window.location.href = signInHref(returnTo);
    }, 400);
    return () => clearTimeout(timer);
  }, [returnTo]);

  return (
    <Shell>
      <Eyebrow>{t('vault.mauvely-cloud')}</Eyebrow>
      <h1 style={display(30)}>{t('vault.taking-you-to-sign-in')}</h1>
      <Lead>{t('vault.your-mauvely-account-works-here')}</Lead>
      <div style={{ marginTop: 22 }}>
        <Button variant="primary" size="lg" onClick={() => (window.location.href = signInHref(returnTo))}>
          {t('vault.sign-in')}
        </Button>
      </div>
    </Shell>
  );
}

/**
 * Signed in, but the session has not cleared its second factor.
 *
 * This must not go to /signin. Signing in again mints another session in the
 * same state, and the user can repeat that indefinitely without ever being told
 * what is wrong — the sign-in loop. The second-factor challenge is the only
 * screen that can actually resolve it.
 */
export function SecondFactorRequired() {
  const t = useT();
  const { Button } = ds();
  const returnTo = typeof window !== 'undefined' ? window.location.href : '';
  const href = `${accountOrigin()}/signin/two-factor?next=${encodeURIComponent(returnTo)}`;

  useEffect(() => {
    const timer = setTimeout(() => {
      window.location.href = href;
    }, 400);
    return () => clearTimeout(timer);
  }, [href]);

  return (
    <Shell>
      <Eyebrow>{t('vault.mauvely-cloud')}</Eyebrow>
      <h1 style={display(30)}>{t('vault.one-more-step')}</h1>
      <Lead>{t('vault.finish-the-twofactor-challenge-and')}</Lead>
      <div style={{ marginTop: 22 }}>
        <Button variant="primary" size="lg" onClick={() => (window.location.href = href)}>
          {t('vault.continue')}
        </Button>
      </div>
    </Shell>
  );
}

/**
 * The server could not be reached, or answered in a way we cannot attribute to
 * the session.
 *
 * Deliberately not a bounce to sign-in. The user is very probably signed in
 * already, and sending them away on a transient fault is what made a D1 hiccup
 * look like a broken account.
 */
export function Unreachable() {
  const t = useT();
  const { Button } = ds();
  const { loadError, retry } = useApp();

  return (
    <Shell>
      <Eyebrow>{t('vault.mauvely-cloud')}</Eyebrow>
      <h1 style={display(30)}>{t('vault.could-not-reach-your-cloud')}</h1>
      <Lead>{loadError ?? 'Something went wrong on the way to the server.'}</Lead>
      <Lead>{t('vault.this-is-a-connection-problem')}</Lead>
      <div style={{ marginTop: 22 }}>
        <Button variant="primary" size="lg" onClick={retry}>
          {t('vault.try-again')}
        </Button>
      </div>
    </Shell>
  );
}

/**
 * Where an unauthenticated visitor is sent to sign in.
 *
 * Somebody redirected off their company's own domain arrives on `/?org=<slug>`
 * and goes to that company's sign-in page rather than Mauvely's. It is the only
 * way the branding survives the round trip, and without it a customer's people
 * follow their employer's link and land on somebody else's product.
 *
 * The slug is a **hint and nothing more**. It picks a sign-in page; it decides
 * nothing about who this person is or what they may open, and the shape check
 * is there so a query string cannot write an arbitrary path onto the accounts
 * origin. Anything else renders as Mauvely, which is the page they would have
 * had.
 *
 * ── `/o/<slug>` is also read here, not only `?org=` ──────────────────────────
 * The retired path is only rewritten to `?org=` by `RedirectHome` in App.tsx,
 * and that component is reached through the signed-in gate — a visitor who
 * opens a bookmarked `/o/northgate` while signed out never runs it, and hits
 * `<SignedOut/>` on the raw path instead. Reading the path here as a second
 * source is what still brands that visitor's sign-in.
 */
const ORG_HINT = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

const signInHref = (returnTo: string): string => {
  const hint =
    typeof window === 'undefined'
      ? null
      : (new URLSearchParams(window.location.search).get('org') ??
        /^\/o\/([^/]+)/.exec(window.location.pathname)?.[1] ??
        null);
  const path = hint && ORG_HINT.test(hint) ? `/o/${hint}/signin` : '/signin';
  return `${accountOrigin()}${path}?next=${encodeURIComponent(returnTo)}`;
};

/** mauvely.com in production, the local main site in development. Cloud runs on
 *  8788 and the main site on 8787, which is the only pairing that matters here. */
export function accountOrigin(): string {
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:8787`;
  return 'https://mauvely.com';
}

/**
 * Sign out from inside Cloud.
 *
 * The session belongs to mauvely.com, so Cloud cannot end it; what it can end
 * is what this browser holds. The trusted-device key goes first — left behind,
 * it would open the vault on the next load for whoever signs in here — and then
 * the account page takes over.
 */
export async function signOutOfCloud(): Promise<void> {
  await signOut();
  window.location.assign(`${accountOrigin()}/account`);
}

/** Referenced by the settings screen; kept beside the other key ceremonies. */
export { generateNodeKey, SHADOW };
